/**
 * Map Styles Configuration
 * Centralized configuration for all map style options.
 * Define once here, use everywhere in the application.
 */

// =============================================================================
// Theme-specific Base Map Styles (for theme switching)
// =============================================================================
export const THEME_MAP_STYLES = {
    dark: 'mapbox://styles/mapbox/dark-v11',
    light: 'mapbox://styles/mapbox/light-v11',
} as const

// Legacy exports for backward compatibility
export const MAP_STYLES = THEME_MAP_STYLES
export const MAP_STYLE = MAP_STYLES.dark

// =============================================================================
// Map Style Options (for user selection)
// =============================================================================
export type MapStyleId = 'default' | 'satellite' | 'terrain' | 'streets' | '3d-view' | 'navigation'

export interface MapStyleOption {
    id: MapStyleId
    name: string
    description: string
    /** 
     * Style URL - 'theme' means use theme-based style (dark/light)
     * Otherwise use Mapbox style URL directly
     */
    styleUrl: string | 'theme'
    /** Icon name for UI display (using Lucide icons) */
    icon: 'map' | 'satellite' | 'mountain' | 'map-pin' | 'box' | 'navigation'
    /** Whether this style supports theme switching */
    supportsTheme: boolean
    /** Recommended for specific climate risk use cases */
    recommendedFor: ('flood' | 'heat' | 'storm' | 'general')[]
}

/**
 * Available map styles for climate risk visualization.
 * Default style uses theme-based switching (dark/light mode).
 * Other styles provide specific visualization contexts suitable for different analysis needs.
 */
export const MAP_STYLE_OPTIONS: MapStyleOption[] = [
    {
        id: 'default',
        name: 'Default',
        description: 'Theme-aware style (dark/light mode)',
        styleUrl: 'theme',
        icon: 'map',
        supportsTheme: true,
        recommendedFor: ['general'],
    },
    {
        id: 'satellite',
        name: 'Satellite',
        description: 'Satellite imagery for terrain context',
        styleUrl: 'mapbox://styles/mapbox/satellite-streets-v12',
        icon: 'satellite',
        supportsTheme: false,
        recommendedFor: ['flood', 'storm'],
    },
    {
        id: 'terrain',
        name: 'Terrain',
        description: 'Elevation and terrain features',
        styleUrl: 'mapbox://styles/mapbox/outdoors-v12',
        icon: 'mountain',
        supportsTheme: false,
        recommendedFor: ['flood', 'storm'],
    },
    {
        id: 'streets',
        name: 'Streets',
        description: 'Infrastructure and road networks',
        styleUrl: 'mapbox://styles/mapbox/streets-v12',
        icon: 'map-pin',
        supportsTheme: false,
        recommendedFor: ['heat', 'general'],
    },
    {
        id: '3d-view',
        name: '3D View',
        description: 'Urban context with 3D buildings',
        styleUrl: 'mapbox://styles/mapbox/standard',
        icon: 'box',
        supportsTheme: true,
        recommendedFor: ['general', 'heat'],
    },
    {
        id: 'navigation',
        name: 'Navigation',
        description: 'Clean navigation-focused style',
        styleUrl: 'mapbox://styles/mapbox/navigation-day-v1',
        icon: 'navigation',
        supportsTheme: false,
        recommendedFor: ['general'],
    },
]

/** Default map style ID */
export const DEFAULT_MAP_STYLE_ID: MapStyleId = 'default'

/** Get map style option by ID */
export const getMapStyleById = (id: MapStyleId): MapStyleOption | undefined =>
    MAP_STYLE_OPTIONS.find(style => style.id === id)

/** Get map style URL, resolving 'theme' to actual theme style */
export const getMapStyleUrl = (id: MapStyleId, theme: 'dark' | 'light'): string => {
    const style = getMapStyleById(id)
    if (!style) return THEME_MAP_STYLES[theme]
    if (style.styleUrl === 'theme') return THEME_MAP_STYLES[theme]
    return style.styleUrl
}
